import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { endOwnSession, listOwnExtensions, listOwnSessions, revokeOwnExtension } from '../../api/auth';
import { ChangePasswordForm } from '../../components/account/ChangePasswordForm';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { useToast } from '../../components/ui/toast';
import { useSession } from '../../hooks/use-session';

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString('ru-RU') : '—';
}

/**
 * «Мой доступ» — available to every role: change the password, see where the
 * account is signed in and which extensions are connected, and end any of them.
 */
export function AccountPage() {
  const { me } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [changed, setChanged] = useState<number | null>(null);

  const sessions = useQuery({ queryKey: ['account', 'sessions'], queryFn: listOwnSessions, refetchOnWindowFocus: false });
  const extensions = useQuery({ queryKey: ['account', 'extensions'], queryFn: listOwnExtensions, refetchOnWindowFocus: false });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['account'] });
  const onError = (err: unknown) => toast.push(err instanceof Error ? err.message : 'Не удалось выполнить действие');
  const endSession = useMutation({ mutationFn: endOwnSession, onSuccess: refresh, onError });
  const revokeExtension = useMutation({ mutationFn: revokeOwnExtension, onSuccess: refresh, onError });

  return (
    <div className="h-full overflow-y-auto">
      <div className="border-b border-border px-4 py-3">
        <h1 className="text-lg font-semibold">Мой доступ</h1>
        {me && <div className="text-xs text-muted-fg">{me.user.email}</div>}
      </div>
      <div className="max-w-3xl space-y-4 p-4">
        <Card>
          <CardHeader className="font-medium">Пароль</CardHeader>
          <CardBody className="max-w-sm">
            {changed !== null && (
              <p role="status" className="mb-3 text-sm text-emerald-700">
                Пароль изменён. Завершено других сессий: {changed}.
              </p>
            )}
            <ChangePasswordForm
              email={me?.user.email}
              onChanged={(n) => {
                setChanged(n);
                refresh();
              }}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="font-medium">Сессии в кабинете</CardHeader>
          <CardBody>
            <p className="mb-2 text-xs text-muted-fg">
              Сессия живёт 14 дней с последнего обращения. Незнакомую сессию завершите и смените пароль.
            </p>
            <ul className="divide-y divide-border text-sm">
              {(sessions.data?.sessions ?? []).map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate">
                      {s.user_agent ?? 'Неизвестное устройство'} {s.current && <Badge>эта сессия</Badge>}
                    </div>
                    <div className="text-xs text-muted-fg">
                      IP {s.ip ?? '—'} · вход {when(s.created_at)} · активность {when(s.last_seen_at)}
                    </div>
                  </div>
                  {!s.current && (
                    <Button size="sm" variant="secondary" onClick={() => endSession.mutate(s.id)} disabled={endSession.isPending}>
                      Завершить
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="font-medium">Подключённые расширения</CardHeader>
          <CardBody>
            {(extensions.data?.extensions ?? []).length === 0 && <p className="text-sm text-muted-fg">Расширение не подключено.</p>}
            <ul className="divide-y divide-border text-sm">
              {(extensions.data?.extensions ?? []).map((x) => (
                <li key={x.id} className="flex items-center justify-between gap-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate">Расширение {x.ext_version ? `v${x.ext_version}` : ''}</div>
                    <div className="text-xs text-muted-fg">
                      подключено {when(x.connected_at)} · последняя активность {when(x.last_used_at)}
                    </div>
                  </div>
                  <Button size="sm" variant="secondary" onClick={() => revokeExtension.mutate(x.id)} disabled={revokeExtension.isPending}>
                    Отозвать
                  </Button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
